import { useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { TResponse } from "../../../types/common";
import { ICountry, ICity, IState } from "../../../types/api/location";
import { fetchCountries, fetchStates, fetchCities } from "./callbacks";

export const useCountries = (
  params?: { search?: string; limit?: number },
  options?: Omit<UseQueryOptions<TResponse<ICountry[]>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponse<ICountry[]>, Error> => {
  return useQuery<TResponse<ICountry[]>, Error>({
    queryKey: [API_ENDPOINTS.LOCATION_COUNTRIES, params?.search ?? "", params?.limit ?? 0],
    queryFn: async () => {
      const res = await fetchCountries(params);
      return res.data;
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useStates = (
  country_id: number | undefined,
  params?: { search?: string; limit?: number },
  options?: Omit<UseQueryOptions<TResponse<IState[]>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponse<IState[]>, Error> => {
  return useQuery<TResponse<IState[]>, Error>({
    queryKey: [API_ENDPOINTS.LOCATION_STATES, country_id, params?.search ?? "", params?.limit ?? 0],
    queryFn: async () => {
      const res = await fetchStates(country_id!, params);
      return res.data;
    },
    enabled: !!country_id,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useCities = (
  state_id: number | undefined,
  params?: { search?: string; limit?: number },
  options?: Omit<UseQueryOptions<TResponse<ICity[]>, Error>, "queryKey" | "queryFn">
): UseQueryResult<TResponse<ICity[]>, Error> => {
  return useQuery<TResponse<ICity[]>, Error>({
    queryKey: [API_ENDPOINTS.LOCATION_CITIES, state_id, params?.search ?? "", params?.limit ?? 0],
    queryFn: async () => {
      const res = await fetchCities(state_id!, params);
      return res.data;
    },
    enabled: !!state_id,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    ...options,
  });
};
